import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import toast from 'react-hot-toast';
import { offlineEnabled, graceValidToday } from '../utils/offlineCache';
import { flushQueue, queueCount, onQueueChange, autoQueuePendingOffline, discardFailedOps, failedCount } from '../utils/offlineQueue';
import { isOnline as netIsOnline } from '../utils/netStatus';
import { requestResync } from '../utils/netMonitor';
import { syncOfflineAuth } from '../utils/offlineAuth';
import { confirm } from './ConfirmDialog';
import useAuthStore from '../store/authStore';

// İnternet gedəndə (offline rejim tikli tenantda) yuxarıda bildiriş zolağı +
// online qayıdanda offline növbəni avtomatik serverə göndərir ("hər şey sinxronlaşdırıldı").
// İlişən op olsa → banner "yenidən / ləğv" düymələri verir (heç vaxt əbədi "syncing" qalmır).
const OfflineBanner = () => {
    const [online, setOnline] = useState(netIsOnline());
    const [pending, setPending] = useState(queueCount());
    const [flushing, setFlushing] = useState(false);
    const [lastErr, setLastErr] = useState('');
    const flushingRef = useRef(false);

    const doFlush = async () => {
        if (flushingRef.current) return;
        if (!netIsOnline() || !offlineEnabled()) return;
        try { autoQueuePendingOffline(); } catch { /* ignore */ }
        if (queueCount() === 0) { setLastErr(''); return; }
        flushingRef.current = true;
        setFlushing(true);
        try {
            const r = await flushQueue();
            if (r.synced > 0) {
                toast.success(`✓ Hər şey sinxronlaşdırıldı (${r.synced} əməliyyat).`, { duration: 4000 });
                setLastErr('');
                // Masalar/sifariş ekranları serverdən təzə məlumatı çəksin (status dərhal yenilənsin)
                try { window.dispatchEvent(new CustomEvent('offline-synced', { detail: r.synced })); } catch { /* ignore */ }
            }
            if (r.noAuth) {
                setLastErr('giriş tələb olunur');
            } else if (r.failed > 0) {
                const first = r.errors && r.errors[0];
                setLastErr(first ? `${first.type}: ${first.error}` : 'sinxron alınmadı');
            } else if (r.synced > 0) {
                setLastErr('');
            }
        } finally {
            flushingRef.current = false;
            setFlushing(false);
        }
    };

    // YALNIZ serverin rədd etdiyi op-lar çıxarılır (arxivə yazılır). Digər satışlar növbədə qalır.
    const clearStuck = async () => {
        const n = failedCount();
        if (!n) { toast('Rədd edilmiş əməliyyat yoxdur. Digərləri sinxron gözləyir.'); return; }
        const ok = await confirm({
            title: 'Rədd edilmiş əməliyyatlar',
            message: `${n} əməliyyat server tərəfindən qəbul edilmədi${lastErr ? ` (${lastErr})` : ''}. Onları növbədən çıxaraq? Digər əməliyyatlar qalacaq.`,
            tone: 'danger',
        });
        if (!ok) return;
        discardFailedOps();
        setPending(queueCount());
        setLastErr('');
        toast.success(`${n} əməliyyat növbədən çıxarıldı.`);
    };

    useEffect(() => {
        const sync = () => { const on = netIsOnline(); setOnline(on); if (on) doFlush(); };
        // netMonitor keçidi: online olan kimi ƏVVƏL növbəni sinxronlaşdır, SONRA ekranlar serverdən təzələnsin
        const onNet = async (e) => {
            const on = !!e?.detail?.online;
            setOnline(on);
            if (!on) return; // offline: netMonitor özü 'app-resync' offline yayır
            try { await doFlush(); } catch { /* ignore */ }
            // Offline girişi real online sessiyaya çevir (ekranlar təzələnməmişdən ƏVVƏL)
            try { await useAuthStore.getState().upgradeOfflineSession?.(); } catch { /* ignore */ }
            requestResync('online');
            syncOfflineAuth();
        };
        window.addEventListener('net-status', onNet);
        window.addEventListener('online', sync);
        window.addEventListener('offline', sync);
        const off = onQueueChange(setPending);
        sync();
        // Hər 5 san: status-u yenilə (C#-da hadisələr işləməyə bilər) + online+gözləyən varsa sakit flush
        const iv = setInterval(() => {
            setOnline(netIsOnline());
            if (netIsOnline() && offlineEnabled() && queueCount() > 0 && !flushingRef.current) doFlush();
        }, 5000);
        return () => { window.removeEventListener('net-status', onNet); window.removeEventListener('online', sync); window.removeEventListener('offline', sync); off(); clearInterval(iv); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!offlineEnabled()) return null;

    // ── Online, amma gözləyən offline əməliyyat var ──
    if (online) {
        if (pending <= 0) return null;
        // Aktiv sinxron gedir
        if (flushing && !lastErr) {
            return (
                <BarShell className="fixed inset-x-0 top-0 z-[2000] px-3 py-1.5 text-center text-xs font-bold text-white shadow-md bg-sky-600">
                    🔄 {pending} offline əməliyyat sinxronlaşdırılır...
                </BarShell>
            );
        }
        // İlişib / xəta — hərəkətli (yenidən cəhd / ləğv)
        return (
            <BarShell className="fixed inset-x-0 top-0 z-[2000] px-3 py-1.5 text-center text-[11px] font-bold text-white shadow-md bg-amber-600 flex items-center justify-center gap-2 flex-wrap">
                <span>⚠️ {pending} əməliyyat gözləyir{lastErr ? ` (${lastErr})` : ''}</span>
                <button onClick={doFlush} className="rounded bg-white/25 px-2 py-0.5 hover:bg-white/40">↻ Yenidən</button>
                {failedCount() > 0 && (
                    <button onClick={clearStuck} className="rounded bg-white/25 px-2 py-0.5 hover:bg-white/40">✕ Ləğv</button>
                )}
            </BarShell>
        );
    }

    // ── Offline (internet yox) ──
    const graceOk = graceValidToday();
    return (
        <BarShell className={`fixed inset-x-0 top-0 z-[2000] px-3 py-1.5 text-center text-xs font-bold text-white shadow-md ${graceOk ? 'bg-amber-500' : 'bg-rose-600'}`}>
            {graceOk
                ? `🔴 Offline rejim. Dəyişikliklər internet qayıdanda sinxronlaşacaq${pending > 0 ? ` (${pending} gözləyir)` : ''}`
                : '⚠️ İnternet yoxdur və lisenziya bu gün üçün yoxlanmayıb. Zəhmət olmasa internetə qoşulun.'}
        </BarShell>
    );
};


// Zolaq səhifənin ÜSTÜNƏ düşməsin: hündürlüyünü ölçüb <html>-ə --offline-bar dəyişəni + has-offline-bar
// sinfi qoyur. index.css bu sinifə görə body-ni aşağı itələyir, yapışqan başlıqları zolağın altına salır,
// tam ekran (h-screen) səhifələri qısaldır → aşağıdakı düymələr ekrandan çıxmır.
const BarShell = ({ className, children }) => {
    const ref = useRef(null);
    useLayoutEffect(() => {
        const el = ref.current;
        const rootEl = document.documentElement;
        if (!el) return undefined;
        const apply = () => {
            rootEl.style.setProperty('--offline-bar', `${el.offsetHeight}px`);
            rootEl.classList.add('has-offline-bar');
        };
        apply();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
        if (ro) ro.observe(el);
        return () => {
            if (ro) ro.disconnect();
            rootEl.classList.remove('has-offline-bar');
            rootEl.style.removeProperty('--offline-bar');
        };
    }, []);
    return (
        <div ref={ref} className={className} style={{ paddingTop: 'max(6px, env(safe-area-inset-top))' }}>
            {children}
        </div>
    );
};

export default OfflineBanner;
